import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import {
  surveysApi,
  type Question,
  type QuestionCondition,
  type QuestionType,
  type SurveyStatus,
} from '../features/surveys/api.ts';

const TYPES: QuestionType[] = ['TEXT', 'NUMBER', 'SELECT', 'CHOICE', 'MULTISELECT', 'DATE', 'COUNT_GROUP', 'PRIORITY'];
const NEEDS_OPTIONS = (t: QuestionType): boolean => t === 'SELECT' || t === 'CHOICE' || t === 'MULTISELECT' || t === 'COUNT_GROUP' || t === 'PRIORITY';

const TYPE_LABEL: Record<QuestionType, string> = {
  TEXT: 'Text',
  NUMBER: 'Number',
  SELECT: 'Dropdown',
  CHOICE: 'Single choice',
  MULTISELECT: 'Multiple choice',
  DATE: 'Date',
  COUNT_GROUP: 'Count group',
  PRIORITY: 'Priority order',
};

const STATUS_LABEL: Record<SurveyStatus, string> = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  CLOSED: 'Closed',
};

interface DraftState {
  label: string;
  type: QuestionType;
  required: boolean;
  options: string[];
  condition: QuestionCondition | null;
}

function emptyDraft(): DraftState {
  return { label: '', type: 'TEXT', required: false, options: [], condition: null };
}

function questionToDraft(q: Question): DraftState {
  return { label: q.label, type: q.type, required: q.required, options: q.options ?? [], condition: q.condition };
}

/** Choice questions that come before this one — the only valid skip-logic triggers. */
function eligibleTriggers(questions: Question[], editing: Question | null): Question[] {
  const limit = editing ? editing.orderIndex : Infinity;
  return questions.filter((q) => (q.type === 'SELECT' || q.type === 'CHOICE') && q.orderIndex < limit && q.id !== editing?.id);
}

/** Add / edit a question within the survey. */
function QuestionForm({
  surveyId,
  editing,
  questions,
  onDone,
  onCancel,
}: {
  surveyId: string;
  editing: Question | null;
  questions: Question[];
  onDone: () => void;
  onCancel: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<DraftState>(() => (editing ? questionToDraft(editing) : emptyDraft()));
  const [error, setError] = useState<string | null>(null);

  const invalidate = (): Promise<void> => qc.invalidateQueries({ queryKey: ['survey', surveyId] });

  const save = useMutation({
    mutationFn: () => {
      const options = NEEDS_OPTIONS(draft.type)
        ? draft.options.map((o) => o.trim()).filter(Boolean)
        : undefined;
      if (editing) {
        return surveysApi.updateQuestion(surveyId, editing.id, {
          label: draft.label.trim(),
          required: draft.required,
          options: NEEDS_OPTIONS(draft.type) ? options ?? [] : undefined,
          condition: draft.condition,
        });
      }
      return surveysApi.addQuestion(surveyId, {
        type: draft.type,
        label: draft.label.trim(),
        required: draft.required,
        ...(options ? { options } : {}),
        ...(draft.condition ? { condition: draft.condition } : {}),
      });
    },
    onSuccess: () => { void invalidate(); onDone(); },
    onError: () => setError('Something went wrong. Please try again.'),
  });

  const cleanOptions = draft.options.map((o) => o.trim()).filter(Boolean);
  const optionsInvalid = NEEDS_OPTIONS(draft.type) && cleanOptions.length === 0;

  const triggers = eligibleTriggers(questions, editing);
  const trigger = triggers.find((t) => t.id === draft.condition?.questionId) ?? null;
  const triggerOptions = trigger?.options ?? [];

  const setTrigger = (questionId: string): void =>
    setDraft((d) => ({ ...d, condition: questionId ? { questionId, equals: '' } : null }));
  const setTriggerValue = (equals: string): void =>
    setDraft((d) => (d.condition ? { ...d, condition: { ...d.condition, equals } } : d));

  const setOption = (i: number, val: string): void =>
    setDraft((d) => ({ ...d, options: d.options.map((o, idx) => (idx === i ? val : o)) }));
  const addOption = (): void => setDraft((d) => ({ ...d, options: [...d.options, ''] }));
  const removeOption = (i: number): void =>
    setDraft((d) => ({ ...d, options: d.options.filter((_, idx) => idx !== i) }));

  return (
    <form
      className="space-y-3 rounded-lg border border-border bg-muted/20 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        if (!draft.label.trim()) return;
        if (optionsInvalid) { setError('At least one option is required.'); return; }
        if (draft.condition && !draft.condition.equals) { setError('Choose the answer that should show this question.'); return; }
        save.mutate();
      }}
    >
      <div className="space-y-1">
        <label className="field-label">Label</label>
        <input
          required
          className="input"
          value={draft.label}
          onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <label className="field-label">Type</label>
          <select
            className="input"
            value={draft.type}
            disabled={editing !== null}
            onChange={(e) => setDraft((d) => ({ ...d, type: e.target.value as QuestionType }))}
          >
            {TYPES.map((t) => (
              <option key={t} value={t}>{TYPE_LABEL[t]}</option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 pt-6 text-sm">
          <input
            type="checkbox"
            checked={draft.required}
            onChange={(e) => setDraft((d) => ({ ...d, required: e.target.checked }))}
          />
          <span>Required</span>
        </label>
      </div>

      {NEEDS_OPTIONS(draft.type) && (
        <div className="space-y-2">
          <label className="field-label">Options</label>
          {draft.options.map((opt, i) => (
            <div key={i} className="flex gap-2">
              <input
                className="input flex-1"
                placeholder="Option..."
                value={opt}
                onChange={(e) => setOption(i, e.target.value)}
              />
              <button
                type="button"
                onClick={() => removeOption(i)}
                className="btn-ghost px-2 py-1 text-xs shrink-0"
                aria-label="Remove option"
              >
                <Icon name="close" size={14} />
              </button>
            </div>
          ))}
          <button type="button" onClick={addOption} className="btn-ghost px-2 py-1 text-xs">
            <Icon name="add" size={14} /> Add option
          </button>
        </div>
      )}

      {triggers.length > 0 && (
        <div className="space-y-2 rounded-lg border border-dashed border-border p-3">
          <label className="field-label">Show only if…</label>
          <div className="grid gap-2 sm:grid-cols-2">
            <select
              className="input"
              value={draft.condition?.questionId ?? ''}
              onChange={(e) => setTrigger(e.target.value)}
            >
              <option value="">Always show</option>
              {triggers.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
            {trigger && (
              <select
                className="input"
                value={draft.condition?.equals ?? ''}
                onChange={(e) => setTriggerValue(e.target.value)}
              >
                <option value="">Choose answer…</option>
                {triggerOptions.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            )}
          </div>
          {trigger && (
            <p className="text-xs text-muted-foreground">
              This question appears only when “{trigger.label}” is “{draft.condition?.equals || '…'}”.
            </p>
          )}
        </div>
      )}

      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={save.isPending || !draft.label.trim() || optionsInvalid || (draft.condition !== null && !draft.condition.equals)}
          className="btn-primary"
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>
      </div>
    </form>
  );
}

/** A single question row in the survey. */
function QuestionRow({
  surveyId,
  question,
  questions,
  index,
  canUp,
  canDown,
}: {
  surveyId: string;
  question: Question;
  questions: Question[];
  index: number;
  canUp: boolean;
  canDown: boolean;
}): JSX.Element {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const invalidate = (): Promise<void> => qc.invalidateQueries({ queryKey: ['survey', surveyId] });

  const remove = useMutation({
    mutationFn: () => surveysApi.removeQuestion(surveyId, question.id),
    onSuccess: () => { void invalidate(); },
  });

  const reorder = useMutation({
    mutationFn: async (dir: -1 | 1): Promise<void> => {
      const ids = questions.map((q) => q.id);
      const target = index + dir;
      const a = ids[index];
      const b = ids[target];
      if (a === undefined || b === undefined) return;
      ids[index] = b;
      ids[target] = a;
      await surveysApi.reorderQuestions(surveyId, ids);
    },
    onSuccess: () => { void invalidate(); },
  });

  if (editing) {
    return (
      <li className="p-3">
        <QuestionForm
          surveyId={surveyId}
          editing={question}
          questions={questions}
          onDone={() => setEditing(false)}
          onCancel={() => setEditing(false)}
        />
      </li>
    );
  }

  return (
    <li className="px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium leading-snug truncate">
            {question.label}
            {question.required && (
              <span className="pill pill-active ms-2">
                Required
              </span>
            )}
          </p>
          <p className="mono-meta text-muted-foreground truncate">
            {TYPE_LABEL[question.type]}
          </p>
          {question.condition && (
            <p className="mt-0.5 text-xs text-muted-foreground truncate">
              Shown if “{questions.find((t) => t.id === question.condition!.questionId)?.label ?? '—'}” = “{question.condition.equals}”
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => reorder.mutate(-1)}
            disabled={!canUp || reorder.isPending}
            className="btn-ghost px-2 py-1"
            aria-label="Move up"
          >
            <Icon name="expand_less" size={15} />
          </button>
          <button
            type="button"
            onClick={() => reorder.mutate(1)}
            disabled={!canDown || reorder.isPending}
            className="btn-ghost px-2 py-1"
            aria-label="Move down"
          >
            <Icon name="expand_more" size={15} />
          </button>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="btn-ghost px-2 py-1"
            aria-label="Edit question"
          >
            <Icon name="edit" size={15} />
          </button>
          <button
            type="button"
            onClick={() => { if (window.confirm('Delete this question?')) remove.mutate(); }}
            disabled={remove.isPending}
            className="btn-ghost px-2 py-1 text-destructive"
            aria-label="Delete question"
          >
            <Icon name="delete" size={15} />
          </button>
        </div>
      </div>
      {(remove.isError || reorder.isError) && (
        <p className="text-sm text-destructive" role="alert">Something went wrong. Please try again.</p>
      )}
    </li>
  );
}

export function SurveyBuilder(): JSX.Element {
  const { surveyId } = useParams({ from: '/surveys/$surveyId' });
  const qc = useQueryClient();
  const { data: survey, isLoading } = useQuery({
    queryKey: ['survey', surveyId],
    queryFn: () => surveysApi.get(surveyId),
  });

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState('');
  const [addingQuestion, setAddingQuestion] = useState(false);

  const invalidate = (): Promise<void> => qc.invalidateQueries({ queryKey: ['survey', surveyId] });

  const saveTitle = useMutation({
    mutationFn: (title: string) => surveysApi.update(surveyId, { title }),
    onSuccess: () => { void invalidate(); setEditingTitle(false); },
  });

  const saveDesc = useMutation({
    mutationFn: (description: string | null) => surveysApi.update(surveyId, { description }),
    onSuccess: () => { void invalidate(); setEditingDesc(false); },
  });

  const setStatus = useMutation({
    mutationFn: (status: SurveyStatus) => surveysApi.update(surveyId, { status }),
    onSuccess: () => { void invalidate(); },
  });

  const questions = survey?.questions ?? [];

  if (isLoading) {
    return (
      <div className="page">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="page">
        <p className="text-muted-foreground">Survey not found.</p>
      </div>
    );
  }

  return (
    <div className="page space-y-6">
      {/* Header */}
      <div className="space-y-3">
        {/* Title */}
        {editingTitle ? (
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (titleDraft.trim()) saveTitle.mutate(titleDraft.trim());
            }}
          >
            <input
              className="input flex-1 text-xl font-bold"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              autoFocus
            />
            <button
              type="submit"
              disabled={saveTitle.isPending || !titleDraft.trim()}
              className="btn-ghost px-2 py-1"
              aria-label="Save title"
            >
              <Icon name="check" size={16} />
            </button>
            <button
              type="button"
              onClick={() => setEditingTitle(false)}
              className="btn-ghost px-2 py-1"
              aria-label="Cancel"
            >
              <Icon name="close" size={16} />
            </button>
          </form>
        ) : (
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold font-serif leading-tight">{survey.title}</h1>
            <button
              type="button"
              onClick={() => { setTitleDraft(survey.title); setEditingTitle(true); }}
              className="btn-ghost px-2 py-1"
              aria-label="Edit title"
            >
              <Icon name="edit" size={15} />
            </button>
          </div>
        )}
        {saveTitle.isError && (
          <p className="text-sm text-destructive" role="alert">Could not save title. Please try again.</p>
        )}

        {/* Description */}
        {editingDesc ? (
          <form
            className="flex items-start gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              saveDesc.mutate(descDraft.trim() || null);
            }}
          >
            <textarea
              className="input flex-1"
              rows={3}
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              autoFocus
            />
            <div className="flex flex-col gap-1">
              <button
                type="submit"
                disabled={saveDesc.isPending}
                className="btn-ghost px-2 py-1"
                aria-label="Save description"
              >
                <Icon name="check" size={16} />
              </button>
              <button
                type="button"
                onClick={() => setEditingDesc(false)}
                className="btn-ghost px-2 py-1"
                aria-label="Cancel"
              >
                <Icon name="close" size={16} />
              </button>
            </div>
          </form>
        ) : (
          <div className="flex items-start gap-2">
            <p className="text-sm text-muted-foreground flex-1">
              {survey.description ?? 'No description — click to add one.'}
            </p>
            <button
              type="button"
              onClick={() => { setDescDraft(survey.description ?? ''); setEditingDesc(true); }}
              className="btn-ghost px-2 py-1 shrink-0"
              aria-label="Edit description"
            >
              <Icon name="edit" size={15} />
            </button>
          </div>
        )}
        {saveDesc.isError && (
          <p className="text-sm text-destructive" role="alert">Could not save description. Please try again.</p>
        )}

        {/* Status control + Results link */}
        <div className="flex flex-wrap items-center gap-2">
          {(['DRAFT', 'ACTIVE', 'CLOSED'] as SurveyStatus[]).map((s) => (
            <button
              key={s}
              type="button"
              disabled={setStatus.isPending}
              aria-pressed={survey.status === s}
              onClick={() => { if (survey.status !== s) setStatus.mutate(s); }}
              className={survey.status === s ? 'btn-primary' : 'btn-secondary'}
            >
              {STATUS_LABEL[s]}
            </button>
          ))}
          <Link
            to="/surveys/$surveyId/results"
            params={{ surveyId }}
            className="btn-ghost ms-2"
          >
            View Results
          </Link>
        </div>
        {setStatus.isError && (
          <p className="text-sm text-destructive" role="alert">Could not update status. Please try again.</p>
        )}
      </div>

      {/* Questions */}
      <section className="card-paper">
        <div className="border-b border-border px-4 py-3">
          <h2 className="font-serif text-base font-bold">Questions</h2>
        </div>

        {questions.length === 0 && !addingQuestion && (
          <p className="px-4 py-3 text-sm text-muted-foreground">No questions yet.</p>
        )}

        {questions.length > 0 && (
          <ul className="divide-y divide-border">
            {questions.map((q, i) => (
              <QuestionRow
                key={q.id}
                surveyId={surveyId}
                question={q}
                questions={questions}
                index={i}
                canUp={i > 0}
                canDown={i < questions.length - 1}
              />
            ))}
          </ul>
        )}

        <div className="p-4">
          {addingQuestion ? (
            <QuestionForm
              surveyId={surveyId}
              editing={null}
              questions={questions}
              onDone={() => setAddingQuestion(false)}
              onCancel={() => setAddingQuestion(false)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setAddingQuestion(true)}
              className="btn-secondary"
            >
              <Icon name="add" size={15} /> Add question
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
